import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgPaper } from "@intelligems/ig-theme";
import { AnalyticsResourceType, MetricArrayType } from "@intelligems/ig-types";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

import { setDrawer } from "../../store/slices/global";
import { IAppState } from "../../store/types/store";
import {
  BarMetricData,
  HistogramMetricData,
  StackedAreaMetricData,
  StackedBarMetricData,
} from "../../types/analytics";
import { ExperienceFe } from "../../types/schemas/experiences";

import { GraphConfig } from "./Graphs/Utils/graphConfigs";
import { GraphRender } from "./Utils/GraphRender";
import { SingleGraphHeader } from "./SingleGraphHeader";

const PREFIX = "SingleGraphWrapper";

const classes = {
  dragIcon: `${PREFIX}-dragIcon`,
  graphPaper: `${PREFIX}-graphPaper`,
  fontFamily: `${PREFIX}-fontFamily`,
  loadingData: `${PREFIX}-loadingData`,
};

const StyledPaper = styled(IgPaper)(({ theme }) => ({
  [`& .${classes.dragIcon}`]: {
    color: "rgba(0,0,0,0.38)",
    transform: "rotate(90deg)",
    "&:hover, &:focus": {
      color: theme.palette.primary.dark,
      transform: "rotate(90deg) scale(1.25)",
    },
  },

  [`& .${classes.graphPaper}`]: {
    padding: "30px",
  },

  [`& .${classes.fontFamily}`]: {
    fontFamily: theme.typography.fontFamily,
  },
  [`& .${classes.loadingData}`]: {
    marginTop: "150px",
    textAlign: "center",
  },
}));

interface SingleGraphInnerWrapperProps {
  resourceType: AnalyticsResourceType;
  resource?: ExperienceFe;
  data?:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[]
    | StackedAreaMetricData[];
  config: GraphConfig;
  totalTestVisitors?: number;
  graphMetricTotals?: MetricArrayType;
  metricFilterIndex?: number;
  isDrawerRender?: boolean;
}
export const SingleGraphInnerWrapper = ({
  resource,
  data,
  config,
  totalTestVisitors,
  resourceType,
  graphMetricTotals,
  metricFilterIndex,
  isDrawerRender,
}: SingleGraphInnerWrapperProps) => {
  const [aovFilterChoice, setAovFilterChoice] = useImmer<string>("all");
  const dispatch = useDispatch();
  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );
  const onMoreDetailsClick = (config: GraphConfig) => {
    dispatch(
      setDrawer({
        drawer: "sitewideAnalyticsViewDetails",
        open: true,
        props: { config },
      })
    );
  };
  return (
    <StyledPaper
      sx={{ minHeight: config.isImpactGraph ? "380px" : "330px" }}
      className={classes.graphPaper}
    >
      {!data ||
      (config.isImpactGraph &&
        dashboardFilters.compareGroupA === undefined &&
        dashboardFilters.compareGroupB === undefined) ? (
        <div className={classes.loadingData}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <SingleGraphHeader
            groupMetricData={data}
            compareGroupA={dashboardFilters.compareGroupA}
            compareGroupB={dashboardFilters.compareGroupB}
            config={config}
            resource={resource}
            resourceType={resourceType}
            aovFilterChoice={aovFilterChoice}
            setAovFilterChoice={setAovFilterChoice}
            totalTestVisitors={totalTestVisitors}
            graphMetricTotals={graphMetricTotals}
            metricFilterIndex={metricFilterIndex}
            onMoreDetailsClick={onMoreDetailsClick}
            isDrawerRender={isDrawerRender}
          />
          <GraphRender
            config={config}
            resource={resource}
            resourceType={resourceType}
            data={data}
            metricFilterIndex={metricFilterIndex}
            aovFilterChoice={aovFilterChoice}
          />
        </>
      )}
    </StyledPaper>
  );
};
